/**
 * toast.js — the real implementation behind AppBroadcast.UI_TOAST.
 *
 * Every route module's local toast() wrapper (magnets-route.js, leads-route.js,
 * ab-testing-route.js, team-route.js, clients-route.js, magnet-flow.js,
 * settings-notifications-route.js), plus the direct PubSub.publish() calls in
 * app_events.js and main.js, already send {message, type} to this one topic.
 * Its subscriber (core/app_events.js) was a `// TODO: Implement toast
 * notification system` + console.log stub, so every one of those calls —
 * including "Magnet created" / "Magnet updated" — has never actually been
 * visible to a user, just a console line nobody sees. This module is wired in
 * at that single subscriber, so no call site anywhere needs to change.
 *
 * Renders a fixed top-right stack, auto-dismisses, doesn't block interaction
 * with anything underneath (pointer-events: none on the stack container itself,
 * auto only on individual toasts so their close button still works).
 */

// Every distinct `type` string actually in use across the app today (audited
// all toast()/PubSub.publish(UI_TOAST) call sites before writing this):
// 'success', 'danger', 'error' (team-route.js/clients-route.js use this
// instead of 'danger' for the same meaning — both map to the same styling
// here rather than requiring those call sites to change), 'info', 'warning'
// (app_events.js/main.js). Anything else falls back to 'info' rather than
// throwing or rendering unstyled — this is fed by ~10 independent call sites
// that must never be able to break the app by passing an unexpected type.
const TYPE_STYLE = {
  success: { border: 'var(--color-success, #009473)', bg: 'var(--color-success-soft, rgba(0,148,115,0.10))', icon: 'check_circle' },
  danger: { border: 'var(--color-danger, #DD4124)', bg: 'var(--color-danger-soft, rgba(221,65,36,0.09))', icon: 'error' },
  error: { border: 'var(--color-danger, #DD4124)', bg: 'var(--color-danger-soft, rgba(221,65,36,0.09))', icon: 'error' },
  warning: { border: 'var(--color-warning, #D97706)', bg: 'var(--color-warning-soft, #FEF3C7)', icon: 'warning' },
  info: { border: 'var(--color-info, #0D9488)', bg: 'var(--color-info-soft, #CCFBF1)', icon: 'info' },
};

const DISMISS_MS = 4500;
const EXIT_MS = 220;

let stack = null;
let stylesInjected = false;

function ensureStyles() {
  if (stylesInjected) return;
  const style = document.createElement('style');
  style.id = 'm5t-toast-styles';
  style.textContent = `
    .m5t-toast-stack { position: fixed; top: 16px; right: 16px; z-index: 6000; display: flex; flex-direction: column; gap: 10px; pointer-events: none; max-width: min(380px, calc(100vw - 32px)); }
    .m5t-toast { pointer-events: auto; display: flex; align-items: flex-start; gap: 10px; padding: 12px 14px; border-radius: var(--radius-md, 10px); background: var(--color-bg, #fff); border: 1px solid; box-shadow: 0 8px 24px rgba(11,18,32,0.16); font-size: 0.86rem; line-height: 1.4; color: var(--color-text, #0B1220); opacity: 0; transform: translateX(16px); transition: opacity ${EXIT_MS}ms ease, transform ${EXIT_MS}ms ease; }
    .m5t-toast.is-in { opacity: 1; transform: none; }
    .m5t-toast.is-out { opacity: 0; transform: translateX(16px); }
    .m5t-toast .material-symbols-outlined { font-size: 19px; flex: 0 0 auto; margin-top: 1px; }
    .m5t-toast__msg { flex: 1 1 auto; min-width: 0; word-break: break-word; }
    .m5t-toast__close { flex: 0 0 auto; appearance: none; border: 0; background: transparent; cursor: pointer; color: var(--color-text-subtle, #6C7C9A); padding: 2px; line-height: 1; font-size: 17px; border-radius: 6px; }
    .m5t-toast__close:hover { color: var(--color-text, #0B1220); background: var(--color-surface-2, rgba(11,18,32,0.06)); }
    @media (prefers-reduced-motion: reduce) { .m5t-toast { transition: none; } }
  `;
  document.head.appendChild(style);
  stylesInjected = true;
}

function ensureStack() {
  if (stack && document.body.contains(stack)) return stack;
  stack = document.createElement('div');
  stack.className = 'm5t-toast-stack';
  // One live region for the whole stack (not per-toast) so a burst of toasts
  // doesn't spam a screen reader with N separate live-region announcements.
  stack.setAttribute('role', 'status');
  stack.setAttribute('aria-live', 'polite');
  document.body.appendChild(stack);
  return stack;
}

/**
 * Show a toast. Accepts the exact {message, type} shape every call site
 * already publishes — no call site needs to change.
 */
export function showToast(data) {
  const message = data && data.message;
  if (!message) return; // nothing to show — don't render an empty bubble
  ensureStyles();

  const style = TYPE_STYLE[data.type] || TYPE_STYLE.info;
  const el = document.createElement('div');
  el.className = 'm5t-toast';
  el.style.borderColor = style.border;
  el.style.background = style.bg;
  el.innerHTML =
    `<span class="material-symbols-outlined" aria-hidden="true" style="color:${style.border}">${style.icon}</span>` +
    `<span class="m5t-toast__msg"></span>` +
    `<button type="button" class="m5t-toast__close" aria-label="Dismiss"><span class="material-symbols-outlined" aria-hidden="true">close</span></button>`;
  // Message text goes through textContent, never innerHTML — it can carry
  // server-derived error text (e.g. err.message), so treat it as untrusted.
  el.querySelector('.m5t-toast__msg').textContent = String(message);

  const container = ensureStack();
  container.appendChild(el);
  requestAnimationFrame(() => el.classList.add('is-in'));

  let dismissed = false;
  const dismiss = () => {
    if (dismissed) return;
    dismissed = true;
    clearTimeout(timer);
    el.classList.remove('is-in');
    el.classList.add('is-out');
    setTimeout(() => el.remove(), EXIT_MS);
  };
  const timer = setTimeout(dismiss, DISMISS_MS);
  el.querySelector('.m5t-toast__close').addEventListener('click', dismiss);
}

export default { showToast };
